import { UserRole } from "../models/user";

import type { FacilityEntity } from "../scenes/Facility/models/facilities";

export const userHasAvailableFacilities = (facilities: FacilityEntity[]) => {
  if (facilities.length === 0) {
    return false;
  }

  return true;
};

export const facilityIsAvailableForUser = (facility: string, facilities: FacilityEntity[]) => {
  if (facilities.find((available) => available.ViewId === facility)) {
    return true;
  }

  return false;
};

export const mapRoleEnumToRole = (roleName: UserRole): string => {
  switch (roleName) {
    case UserRole.ADMIN:
      return "FacilityAdmin";
    case UserRole.POWER:
      return "PowerCoder";
    case UserRole.CODER:
      return "Coder";
    case UserRole.MANAGER:
      return "CaseManager";
    case UserRole.NOROLE:
    default:
      return "NoRole";
  }
};

/**
 * Permission character constants
 */
export const PERMISSIONS = {
  AddModifyDisableUser: "u",
  AssignUserToFacility: "a",
  MaintainFacilityPreferences: "e",
  MaintainFinancialClassConfiguration: "c",
  MaintainChoiceLists: "l",
  GenerateReports: "r",
  MaintainFacilityCodingAdviceSynonymsCodeSetsAndCustomEdits: "i",
  MaintainPersonalCodingAdviceSynonymsCodeSets: "k",
  CreateNewPatientRecord: "n",
  DeletePatientRecord: "d",
  ModifyPatientRecord: "p",
  ViewPatientRecord: "v",
  CreateFacility: "f",
} as const;

/**
 * Checks if a permission string contains a specific permission
 * @param permissions - The permission string (e.g., "vpdnirk")
 * @param permission - The permission character to check (e.g., "n" for CreateNewPatientRecord)
 * @returns true if the permission is present, false otherwise
 */
export const hasPermission = (permissions: string | undefined, permission: string): boolean => {
  if (!permissions) {
    return false;
  }
  return permissions.includes(permission);
};

/**
 * Checks if user can create encounters (has CreateNewPatientRecord permission)
 * @param permissions - The permission string
 * @returns true if user can create encounters
 */
export const canCreateEncounters = (permissions: string | undefined): boolean =>
  hasPermission(permissions, PERMISSIONS.CreateNewPatientRecord);

/**
 * Checks if user can modify patient records (has ModifyPatientRecord permission)
 * @param permissions - The permission string
 * @returns true if user can modify patient records
 */
export const canModifyPatientRecords = (permissions: string | undefined): boolean =>
  hasPermission(permissions, PERMISSIONS.ModifyPatientRecord);

/**
 * Checks if user can view patient records (has ViewPatientRecord permission)
 * @param permissions - The permission string
 * @returns true if user can view patient records
 */
export const canViewPatientRecords = (permissions: string | undefined): boolean =>
  hasPermission(permissions, PERMISSIONS.ViewPatientRecord);

/**
 * Checks if user can delete patient records (has DeletePatientRecord permission)
 * @param permissions - The permission string
 * @returns true if user can delete patient records
 */
export const canDeletePatientRecords = (permissions: string | undefined): boolean =>
  hasPermission(permissions, PERMISSIONS.DeletePatientRecord);
